// Foundations
import {
  Component,
  OnInit,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  AfterViewChecked,
} from '@angular/core';
import { FormBuilder, Validators, FormGroup, FormArray } from '@angular/forms';

// rxjs
import { filter, finalize, map, take } from 'rxjs/operators';

// Components
import { ContactDetailsFormComponent } from '../../../components/_share/contact-details-form/contact-details-form.component';
import { HomeAddressFormComponent } from '../../../components/my-profile/home-address-form/home-address-form.component';

// Services
import { AnalyticsService } from 'src/app/services/analytics.service';
import {
  Account,
  GetAccountDetailsGQL,
  UpdateAccountGQL,
} from 'src/app/services/graphql.service';
import { AuthService } from 'src/app/auth/auth.service';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';

// Helper function
import { cloneAbstractControl } from 'src/app/shared/clone-abstract-control';

const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/;
const PHONE_REGEX = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
const POSTAL_CODE_REGEX =
  /^(\d{5}(\-\d{4})?|[A-Za-z]\d[A-Za-z][ \-]?\d[A-Za-z]\d)$/;

@Component({
  selector: 'app-contact-details-page',
  templateUrl: './contact-details-page.component.html',
  styleUrls: ['./contact-details-page.component.scss'],
})
export class ContactDetailsPageComponent implements OnInit, AfterViewChecked {
  @ViewChild('cdForm', { static: false }) cdForm: ContactDetailsFormComponent;
  @ViewChild('haForm', { static: false }) haForm: HomeAddressFormComponent;

  @Input() isFirstTime: boolean;

  @Output() isEditMode = new EventEmitter<boolean>();
  @Output() navigateToOtherContacts = new EventEmitter<number>();

  contactDetailsForm: FormGroup;
  contactDetailsFormClone: FormGroup;
  homeAddressForm: FormGroup;
  homeAddressFormClone: FormGroup;
  editMode: boolean;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private getAccountDetailsQuery: GetAccountDetailsGQL,
    private updateAccountMutation: UpdateAccountGQL,
    private _analytics: AnalyticsService,
    private loadingIndicatorService: LoadingIndicatorService,
    private cdRef: ChangeDetectorRef
  ) {
    this.contactDetailsForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.pattern(EMAIL_REGEX)]],
      phones: this.fb.array([]),
    });
    this.homeAddressForm = this.fb.group(
      {
        country: [null, Validators.required],
        address1: ['', Validators.required],
        address2: '',
        city: ['', Validators.required],
        stateProvince: [null, Validators.required],
        postalCode: ['', Validators.required],
      },
      { validator: this.checkPostalCode }
    );
  }

  // Life cycles
  // ------------------------------------------------------------------------------------

  ngOnInit() {
    this.fetchData();
  }

  ngAfterViewChecked() {
    this.cdRef.detectChanges();
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  get phoneArray(): FormArray {
    return this.contactDetailsForm.get('phones') as FormArray;
  }

  exitEditMode() {
    this._analytics.logButtonTap('myProfile_saveStepContact');

    this.contactDetailsForm = this.cdForm.getForm();
    this.homeAddressForm = this.haForm.getForm();

    if (this.contactDetailsForm.valid && this.homeAddressForm.valid) {
      const { firstName, lastName, phones } = this.contactDetailsForm.value;
      const { country, address1, address2, city, stateProvince, postalCode } =
        this.homeAddressForm.value;

      const phoneInputList = phones.map((phone) => {
        const { id, geoCountry, phoneType, ...rest } = phone;
        return {
          ...rest,
          ...(!!id && { id }),
          geoCountry: geoCountry.id,
          phoneType: phoneType.id,
        };
      });

      this.loadingIndicatorService.show();

      this.authService.accountId$
        .pipe(
          filter((accountId) => !!accountId),
          take(1)
        )
        .subscribe((accountId) => {
          this.updateAccountMutation
            .mutate({
              accountId,
              accountInput: {
                // contact details
                firstName,
                lastName,
                phones: phoneInputList,
                // home address
                country,
                address1,
                address2,
                city,
                stateProvince,
                postalCode,
              },
              forRegistration: false
            })
            .pipe(finalize(() => this.loadingIndicatorService.hide()))
            .subscribe((res) => {
              if (res.data) {
                this.editMode = false;
                this.isEditMode.emit(this.editMode);
                // populate forms with returned data
                this.populateForms(res.data.updateAccount);
                // mark forms as pristine
                this.contactDetailsForm.markAsPristine();
                this.homeAddressForm.markAsPristine();
                if (this.isFirstTime) {
                  this.navigateToOtherContacts.emit(1);
                }
              }
            });
        });
    }
  }

  enterEditMode() {
    this._analytics.logLinkTap('myProfile_editContact');

    this.editMode = true;
    this.isEditMode.emit(this.editMode);
  }

  resetFormToDefault() {
    this.contactDetailsForm = cloneAbstractControl(
      this.contactDetailsFormClone
    );
    this.homeAddressForm = cloneAbstractControl(this.homeAddressFormClone);
  }

  // Private Methods
  // ------------------------------------------------------------------------------------

  private fetchData() {
    this.authService.accountId$
      .pipe(
        filter((accountId) => !!accountId),
        take(1)
      )
      .subscribe((accountId) => {
        this.getAccountDetailsQuery
          .fetch({ accountId })
          .pipe(map((res) => res.data.getAccount))
          .subscribe((account) => {
            this.populateForms(account);
          });
      });
  }

  private populateForms(account: Partial<Account>) {
    const {
      // contact details
      firstName,
      lastName,
      email,
      phones,
      // home address
      country,
      address1,
      address2,
      city,
      stateProvince,
      postalCode,
    } = account;

    // set to edit mode if any of the form fields is not filled
    this.editMode = !(
      firstName &&
      lastName &&
      email &&
      phones?.length !== 0 &&
      country &&
      address1 &&
      city &&
      stateProvince &&
      postalCode
    );

    // populate contact details
    this.contactDetailsForm.patchValue({ firstName, lastName, email });
    this.phoneArray.clear();
    if (phones?.length) {
      phones.forEach((phone, index) => {
        const { id, geoCountry, phoneNumber, phoneType } = phone;
        this.phoneArray.push(
          this.fb.group({
            id,
            geoCountry: [geoCountry, Validators.required],
            phoneNumber: [
              phoneNumber,
              [Validators.required, Validators.pattern(PHONE_REGEX)],
            ],
            phoneType: [phoneType, Validators.required],
            isPrimary: index === 0,
            isVerified: false,
            marketingSmsOptIn: false,
          })
        );
      });
    }
    this.contactDetailsFormClone = cloneAbstractControl(
      this.contactDetailsForm
    );
    // populate home address
    this.homeAddressForm.patchValue({
      country,
      address1,
      address2,
      city,
      stateProvince,
      postalCode,
    });
    this.homeAddressFormClone = cloneAbstractControl(this.homeAddressForm);
  }

  private checkPostalCode(group: FormGroup) {
    const errors: { [k: string]: any } = {};
    const postalCode = group.controls.postalCode?.value;

    if (!POSTAL_CODE_REGEX.test(postalCode)) {
      errors.postalCode = true;
    }

    return errors;
  }
}
